import { Component, ViewChild, OnInit, ElementRef, AfterViewInit, Input, OnChanges, OnDestroy, Output, EventEmitter } from '@angular/core';
import { AppService } from '../app.service';
import { messageService } from '../message.service';
import { MatSnackBar } from '@angular/material/snack-bar';

declare const WebViewer: any;

@Component({
  selector: 'web-viewer-printer', // ✅ New selector
  templateUrl: './webviewer-print.component.html',
  styleUrls: ['./webviewer-print.component.scss']
})
export class WebviewerPrintComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('viewer', { static: false }) viewer: ElementRef;
  @ViewChild('viewer') viewer1: ElementRef;
  @Output() oneQmsListener = new EventEmitter<void>();


  @Input() data: any = {};
  @Output() documentLoaded = new EventEmitter<void>();
  wvInstance: any;
  viewInitialized = false;
  private isDocLoading = false;
  private printBlockHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private readonly appService: AppService,
    public message: messageService,
    private readonly snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.wvDocumentLoadedHandler = this.wvDocumentLoadedHandler.bind(this);
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;

    if (!this.wvInstance) {
      WebViewer(
        {
          path: '../../wv-resources/lib',
          licenseKey:
            'Clinevo Technologies Private Limited (clinevotech.com):OEM:eTrials::B+:AMS(20231004):FEA5C54D0427360AF360B13AC982737B60610F8BAF461E35E56C05DA5C956EAE0AFAB6F5C7',
          fullAPI: true,
          disableLogs: true,
          webViewerServer: true,
          disabledElements: [
            'leftPanelButton',
            'toolbarGroup-Annotate',
            'highlightToolGroupButton',
            'underlineToolGroupButton',
            'strikeoutToolGroupButton',
            'squigglyToolGroupButton',
            'freeTextToolGroupButton',
            'shapeToolGroupButton',
            'stickyToolGroupButton',
            'freeHandToolGroupButton',
            'toolsOverlay',
            'eraserToolButton',
            'redoButton',
            'stickyToolButton',
            'textPopup',
            'toggleNotesButton',
            'selectToolButton',
            'highlightToolButton',
            'freeHandToolButton',
            'freeTextToolButton',
            'toolsHeader',
            'printButton', // will be conditionally re-enabled
          ]
        },
        this.viewer.nativeElement
      ).then((instance: any) => {
        instance.UI.hotkeys.off('escape');

        // Override WebViewer internal ESC handler:
        instance.UI.hotkeys.on('escape', (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          console.warn("WebViewer ESC blocked.");
          return false;
        });

        this.wvInstance = instance;

        if (this.message.isDocumentDownloader) {
          // ✅ ENABLE PRINT FOR AUTHORIZED USERS
          instance.print = true;
          instance.UI.enableElements(['printButton']);
          instance.UI.enableFeatures([instance.UI.Feature.Print]);
          // Clean up any previous handler
          if (this.printBlockHandler) {
            document.removeEventListener('keydown', this.printBlockHandler);
            this.printBlockHandler = null;
          }
        } else {
          // Disable for unauthorized users
          instance.UI.disableElements(['printButton']);
          instance.UI.disableFeatures([instance.UI.Feature.Print]);
          instance.print = false;

          const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
              e.preventDefault();
              e.stopPropagation();
              this.snackBar.open('Printing is not permitted.', 'Close', { duration: 3000 });
            }
          };

          instance.UI.addEventListener(instance.UI.Events.KEY_DOWN, handleKeyDown);
          document.addEventListener('keydown', handleKeyDown);
          this.printBlockHandler = handleKeyDown;
        }

        // 🔧 Common UI config
        instance.UI.setTheme('dark');
        instance.UI.disableElements([
          'toolbarGroup-Shapes',
          'toolbarGroup-View',
          'toolbarGroup-FillAndSign',
          'toolbarGroup-Forms',
          'toolbarGroup-Edit',
          'toolbarGroup-Insert',
          'menuButton',
          'undoButton',
          'highlightToolGroupButton'
        ]);

        const { documentViewer } = instance.Core;
        // ✅ Use arrow function or bind to ensure correct 'this' context
        documentViewer.addEventListener('documentLoaded', () => {
          this.wvDocumentLoadedHandler(); // Call the bound handler
          this.documentLoaded.emit(); // ✅ EMIT THE EVENT|
          this.registerActivityListeners(instance);
        });

        instance.UI.addEventListener(instance.UI.Events.LOAD_ERROR, (err: any) => {
          console.error('Load Error:', err);
        });

        if (this.data?.body) {
          this.loadDataInViewer(this.data.body, this.data.contentType, this.data.extn);
        }
      });
    } else if (this.data?.body) {
      this.loadDataInViewer(this.data.body, this.data.contentType, this.data.extn);
    }
  }

  ngOnChanges(): void {
    if (!this.viewInitialized || !this.wvInstance || !this.data?.body) {
      return;
    }

    this.loadDataInViewer(this.data.body, this.data.contentType, this.data.extn);
  }


  wvDocumentLoadedHandler(): void {
    console.log('Document loaded');
    this.isDocLoading = false;
  }
  loadDataInViewer(data: any, contentType: string, extension: string): void {
    if (!this.wvInstance?.UI || this.isDocLoading) {
      return;
    }

    this.isDocLoading = true;

    const load = (blob: Blob) => {
      this.wvInstance.UI.loadDocument(blob, {
        extension,
        documentType: extension === 'docx' ? 'office' : undefined
      });
    };

    if (typeof data === 'string') {
      // ✅ async decode (non-blocking)
      fetch(`data:${contentType};base64,${data}`)
        .then(res => res.blob())
        .then(load)
        .catch(() => this.isDocLoading = false);
    } else if (data instanceof Blob) {
      load(data);
    } else {
      load(data);
    }
  }

  ngOnDestroy() {
    if (this.printBlockHandler) {
      document.removeEventListener('keydown', this.printBlockHandler);
      this.printBlockHandler = null;
    }
  }


  private registerActivityListeners(instance: any): void {
    const iframeWindow = instance.UI.iframeWindow;
    if (!iframeWindow) return;

    const activityHandler = () => this.emitActivity();

    // Mouse & keyboard
    iframeWindow.addEventListener('mousemove', activityHandler, { passive: true });
    iframeWindow.addEventListener('mousedown', activityHandler, { passive: true });
    iframeWindow.addEventListener('wheel', activityHandler, { passive: true });
    iframeWindow.addEventListener('keydown', activityHandler);
    // Scroll inside iframe document
    iframeWindow.document.addEventListener('scroll', activityHandler, { passive: true });
  }


  private emitActivity(): void {
    if (!this.isDocLoading && this.wvInstance) {
      this.oneQmsListener.emit();
    }
  }
}
